/**
 * Meta Ads (Facebook + Instagram) connector.
 *
 * Unlike Google Ads, Meta's Graph API exposes CORS headers and authenticates
 * with just the user's OAuth access token. So this connector calls
 * graph.facebook.com directly from the browser — no proxy Worker needed.
 *
 * The Meta App Secret lives only in the
 * @agencylens/worker-meta-oauth-callback Worker; the SPA never sees it.
 *
 * Required permissions (advanced-access after App Review):
 *   - ads_read
 *   - ads_management (read-level use)
 *   - business_management
 */

import type {
  MetaAdAccount,
  MetaAdsReportData,
  MetaAdsDailyRow,
  MetaAdsCampaignRow,
} from "@agencylens/shared";

const GRAPH_VERSION = "v21.0";
const GRAPH_BASE = `https://graph.facebook.com/${GRAPH_VERSION}`;

export class MetaAdsError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "MetaAdsError";
  }
}

/* ----------------------------------------------------- low-level fetch */

async function graphGet<T>(
  accessToken: string,
  pathAndQuery: string,
): Promise<T> {
  const url = new URL(`${GRAPH_BASE}${pathAndQuery}`);
  // Pass token via header for cleanliness; Graph also accepts ?access_token=
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new MetaAdsError(
      `Graph API returned non-JSON ${res.status}: ${text.slice(0, 200)}`,
      res.status,
    );
  }
  if (!res.ok) {
    const obj = data as { error?: { message?: string; type?: string; code?: number } };
    const msg = obj.error?.message ?? `HTTP ${res.status}`;
    throw new MetaAdsError(`Graph API: ${msg}`, res.status);
  }
  return data as T;
}

/* ----------------------------------------------------- listAdAccounts */

interface GraphAdAccountEntry {
  id?: string; // "act_12345"
  account_id?: string; // "12345"
  name?: string;
  currency?: string;
  account_status?: number;
  business?: { id?: string; name?: string };
}

interface GraphPaged<T> {
  data?: T[];
  paging?: { next?: string; cursors?: { after?: string } };
}

export async function listAdAccounts(
  accessToken: string,
): Promise<MetaAdAccount[]> {
  const out: MetaAdAccount[] = [];
  let url = `/me/adaccounts?fields=account_id,name,currency,account_status,business&limit=100`;
  // Follow paging up to a sane cap
  for (let i = 0; i < 10; i++) {
    const data = await graphGet<GraphPaged<GraphAdAccountEntry>>(accessToken, url);
    for (const a of data.data ?? []) {
      if (!a.id) continue;
      out.push({
        adAccountId: a.id,
        name: a.name ?? `Ad Account ${a.account_id ?? a.id}`,
        currency: a.currency ?? "USD",
        accountStatus: a.account_status ?? 0,
        ...(a.business?.id ? { businessId: a.business.id } : {}),
        ...(a.business?.name ? { businessName: a.business.name } : {}),
      });
    }
    const nextCursor = data.paging?.cursors?.after;
    if (!data.paging?.next || !nextCursor) break;
    url = `/me/adaccounts?fields=account_id,name,currency,account_status,business&limit=100&after=${encodeURIComponent(nextCursor)}`;
  }
  return out;
}

/* ----------------------------------------------------- fetchReport */

interface GraphInsightsAction {
  action_type?: string;
  value?: string;
}

interface GraphInsightsRow {
  date_start?: string;
  impressions?: string;
  clicks?: string;
  spend?: string;
  reach?: string;
  actions?: GraphInsightsAction[];
  campaign_id?: string;
  campaign_name?: string;
  objective?: string;
}

function sumActions(
  actions: GraphInsightsAction[] | undefined,
  matchTypes: string[],
): number {
  if (!actions) return 0;
  return actions
    .filter((a) => a.action_type && matchTypes.includes(a.action_type))
    .reduce((s, a) => s + Number(a.value ?? 0), 0);
}

const PURCHASE_TYPES = [
  "purchase",
  "offsite_conversion.fb_pixel_purchase",
  "omni_purchase",
];

const LEAD_TYPES = ["lead", "leadgen.other"];

function rowResults(actions: GraphInsightsAction[] | undefined): number {
  // Prefer purchases as the "results" metric; fall back to leads, then total
  // conversions of any type.
  const purchases = sumActions(actions, PURCHASE_TYPES);
  if (purchases > 0) return purchases;
  const leads = sumActions(actions, LEAD_TYPES);
  if (leads > 0) return leads;
  return sumActions(actions, ["onsite_conversion.purchase", "offsite_conversion"]);
}

export async function fetchReport(
  accessToken: string,
  account: MetaAdAccount,
  startDate: string,
  endDate: string,
): Promise<MetaAdsReportData> {
  const range = encodeURIComponent(JSON.stringify({ since: startDate, until: endDate }));

  // Daily timeseries at the account level
  const dailyPath =
    `/${account.adAccountId}/insights` +
    `?fields=impressions,clicks,spend,reach,actions` +
    `&time_increment=1&level=account&time_range=${range}&limit=500`;

  // Campaign-level totals across the range
  const campaignPath =
    `/${account.adAccountId}/insights` +
    `?fields=campaign_id,campaign_name,objective,impressions,clicks,spend,actions` +
    `&level=campaign&time_range=${range}&limit=100`;

  const [dailyResp, campaignResp] = await Promise.all([
    graphGet<GraphPaged<GraphInsightsRow>>(accessToken, dailyPath),
    graphGet<GraphPaged<GraphInsightsRow>>(accessToken, campaignPath),
  ]);

  const daily: MetaAdsDailyRow[] = (dailyResp.data ?? []).map((r) => ({
    date: r.date_start ?? "",
    impressions: Number(r.impressions ?? 0),
    clicks: Number(r.clicks ?? 0),
    spend: Number(r.spend ?? 0),
    reach: Number(r.reach ?? 0),
    results: rowResults(r.actions),
  }));

  const campaigns: MetaAdsCampaignRow[] = (campaignResp.data ?? [])
    .map((r) => ({
      campaignId: r.campaign_id ?? "",
      campaignName: r.campaign_name ?? "(unnamed)",
      objective: r.objective ?? "UNKNOWN",
      impressions: Number(r.impressions ?? 0),
      clicks: Number(r.clicks ?? 0),
      spend: Number(r.spend ?? 0),
      results: rowResults(r.actions),
    }))
    .filter((r) => r.campaignId)
    .sort((a, b) => b.spend - a.spend);

  const totalImpressions = daily.reduce((s, r) => s + r.impressions, 0);
  const totalClicks = daily.reduce((s, r) => s + r.clicks, 0);
  const totalSpend = daily.reduce((s, r) => s + r.spend, 0);
  const totalReach = daily.reduce((s, r) => s + r.reach, 0);
  const totalResults = daily.reduce((s, r) => s + r.results, 0);

  return {
    account,
    startDate,
    endDate,
    daily,
    campaigns,
    totals: {
      impressions: totalImpressions,
      clicks: totalClicks,
      spend: totalSpend,
      reach: totalReach,
      results: totalResults,
      cpm: totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0,
      cpc: totalClicks > 0 ? totalSpend / totalClicks : 0,
    },
    fetchedAt: new Date().toISOString(),
  };
}

/* ----------------------------------------------------- date helpers */

export function isoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function defaultDateRange(days = 30): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const end = new Date(now);
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

export const META_ADS_SCOPES = [
  "ads_read",
  "ads_management",
  "business_management",
] as const;

/**
 * Space-separated Meta scope string, in the order Facebook's auth dialog
 * displays them.
 */
export const META_ADS_SCOPE = META_ADS_SCOPES.join(",");
